// EditEntryModal — "Upravit záznam" (6.8.2026, přepracováno 19.10.2026, hlášení #166 a #172)
// ---------------------------------------------------------------------------
// Modal pro úpravu už uloženého bloku měření. Pravidla (vynucená i backendem,
// tohle je jen UI, backend je autorita):
//   • do 2 h od konce (entry.editable): činnost, kvadrant, poznámka, smazání i zkrácení
//   • do 7 dnů od konce (entry.zkratitelne): už JEN zkrácení
//   • ownerMode: owner zkracuje cizí záznam (akce ownerZkratit), jen délka
//   • ZAČÁTEK se nemění nikdy, měření jde jen zkrátit (vybere se skutečná délka),
//     musí zbýt aspoň minuta
// Odesílá { action: "editEntry" | "ownerZkratit", id, durationMinutes?, oblast?, qx?, popis? }.
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, Save, Trash2, Loader2, Lock, AlertTriangle, Check } from "lucide-react";
import useEscClose from "../lib/useEscClose.js";

const QX_DESC = {
  Q1: "Urgentní + důležité", Q2: "Důležité, neurgentní",
  Q3: "Urgentní, nedůležité", Q4: "Neurgentní, nedůležité",
};
const toMin = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || "").trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
const toHhmm = (min) => `${Math.floor(((min % 1440) + 1440) % 1440 / 60)}:${String(((min % 60) + 60) % 60).padStart(2, "0")}`;
const fmtDelka = (min) => {
  const h = Math.floor(min / 60), m = min % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
};
const RYCHLE = [15, 30, 45, 60, 90, 120];
const DLOUHE_MIN = 4 * 60;

const labelCls = "text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1";

export default function EditEntryModal({
  open, entry, activities = [], useQuadrants = false, canDelete = true,
  busy = false, onSave, onDelete, onClose, ownerMode = false,
}) {
  const [oblast, setOblast] = useState("");
  const [qx, setQx] = useState("");
  const [popis, setPopis] = useState("");
  const [hod, setHod] = useState("0");
  const [min, setMin] = useState("0");
  const [saving, setSaving] = useState(false);
  const [potvrdSmazat, setPotvrdSmazat] = useState(false);

  useEscClose(onClose, open);

  const puvodniMin = Math.max(0, Math.floor(Number(entry?.duration_sec ?? entry?.sec ?? 0) / 60));

  useEffect(() => {
    if (!open || !entry) return;
    setOblast(entry.oblast || "");
    setQx(entry.qx || "");
    setPopis(entry.popis || "");
    setHod(String(Math.floor(puvodniMin / 60)));
    setMin(String(puvodniMin % 60));
    setSaving(false);
    setPotvrdSmazat(false);
  }, [open, entry?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const plna = !ownerMode && !!entry?.editable;
  const zacatekMin = toMin(entry?.od);
  const delka = (parseInt(hod, 10) || 0) * 60 + (parseInt(min, 10) || 0);
  const nastavDelku = (m) => { setHod(String(Math.floor(m / 60))); setMin(String(m % 60)); };

  const stav = useMemo(() => {
    if (delka === puvodniMin) return { ubere: 0 }; // délka beze změny (i u záznamu kratšího než minuta)
    if (delka < 1) return { err: "Měření musí mít aspoň minutu." };
    if (delka > puvodniMin) return { err: `Prodloužit nejde, nejvíc ${fmtDelka(puvodniMin)}.` };
    return { ubere: puvodniMin - delka };
  }, [delka, puvodniMin]);

  if (!open || !entry) return null;

  const zkracuji = !stav.err && delka < puvodniMin;
  const zmenene = zkracuji || (plna && (
    (oblast || "") !== (entry.oblast || "") ||
    (useQuadrants && (qx || "") !== (entry.qx || "")) ||
    (popis || "") !== (entry.popis || "")));

  const uloz = async () => {
    if (stav.err || saving || busy || !zmenene) return;
    setSaving(true);
    const payload = { action: ownerMode ? "ownerZkratit" : "editEntry", id: entry.id };
    if (zkracuji) payload.durationMinutes = delka;
    if (plna) {
      payload.oblast = oblast;
      payload.popis = popis;
      if (useQuadrants && qx) payload.qx = qx;
    }
    try {
      const ok = await onSave(payload);
      if (ok !== false) onClose();
    } finally { setSaving(false); }
  };

  const rychle = RYCHLE.filter((m) => m < puvodniMin);
  const novyKonec = zacatekMin != null ? toHhmm(zacatekMin + delka) : "?";
  const nadpis = `${entry.datumLabel ? `${entry.datumLabel}, ` : ""}${entry.od || "?"} až ${entry.do || "?"}`;

  return createPortal(
    <div className="nad-klavesnici fixed inset-0 z-[60] bg-brand-dark/50 backdrop-blur-[2px] flex items-end sm:items-center justify-center sm:p-6 overflow-y-auto"
      onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        {/* hlavička */}
        <div className="flex items-center justify-between gap-2 px-4 py-3 rounded-t-2xl bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 text-white sticky top-0 z-10">
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-brand-300">{ownerMode ? `Zkrátit měření${entry.jmeno ? `, ${entry.jmeno}` : ""}` : "Upravit záznam"}</div>
            <div className="font-serif text-lg font-bold tabular-nums truncate">{nadpis}</div>
          </div>
          <button onClick={onClose} aria-label="Zavřít" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 border border-white/20">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {puvodniMin > DLOUHE_MIN && (
            <div className="flex items-start gap-2.5 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-px" />
              <span><strong>Měření běželo {fmtDelka(puvodniMin)}.</strong> {ownerMode ? "Nastav, kolik doopravdy trvalo." : "Zapomněl(a) jsi ho vypnout? Vyber, kolik jsi doopravdy pracoval(a)."}</span>
            </div>
          )}

          {plna && (
            <>
              <div>
                <div className={labelCls}>Činnost</div>
                <select value={oblast} onChange={(e) => setOblast(e.target.value)} className="field">
                  {activities.map((a) => <option key={a.key} value={a.key}>{a.key}</option>)}
                  {!activities.some((a) => a.key === oblast) && oblast && <option value={oblast}>{oblast}</option>}
                </select>
              </div>
              {useQuadrants && (
                <div>
                  <div className={labelCls}>Kvadrant</div>
                  <select value={qx} onChange={(e) => setQx(e.target.value)} className="field">
                    <option value="">Auto dle činnosti</option>
                    {["Q1", "Q2", "Q3", "Q4"].map((x) => <option key={x} value={x}>{x}: {QX_DESC[x]}</option>)}
                  </select>
                </div>
              )}
            </>
          )}

          {/* délka */}
          <div className="space-y-2">
            <div className="text-sm font-bold text-slate-600">{ownerMode ? "Kolik doopravdy pracoval(a)?" : "Kolik jsi doopravdy pracoval(a)?"}</div>
            {rychle.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {rychle.map((m) => (
                  <button key={m} type="button" onClick={() => nastavDelku(m)}
                    className={`min-h-[44px] rounded-xl border text-sm font-semibold transition ${delka === m ? "border-2 border-brand bg-brand-light text-brand-deep font-bold" : "border-slate-300 bg-white text-slate-700 hover:border-brand-300"}`}>
                    {fmtDelka(m)}
                  </button>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-bold text-slate-600">{rychle.length > 0 ? "Nebo přesně" : "Přesně"}</span>
              <div className={`flex items-center gap-2 min-h-[48px] px-3 rounded-xl border-2 ${stav.err ? "border-rose-300 bg-rose-50" : "border-brand bg-brand-50"}`}>
                <input inputMode="numeric" value={hod} onChange={(e) => setHod(e.target.value.replace(/\D/g, "").slice(0, 2))} aria-label="Hodiny"
                  className="w-9 bg-transparent text-right text-xl font-bold text-slate-900 outline-none tabular-nums" />
                <span className="text-sm text-slate-600">h</span>
                <input inputMode="numeric" value={min} onChange={(e) => setMin(e.target.value.replace(/\D/g, "").slice(0, 2))} aria-label="Minuty"
                  className="w-10 bg-transparent text-right text-xl font-bold text-slate-900 outline-none tabular-nums" />
                <span className="text-sm text-slate-600">min</span>
              </div>
            </div>
          </div>

          {stav.err ? (
            <div className="flex items-start gap-2 text-sm font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{stav.err}</span>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2 rounded-xl bg-slate-100 p-3">
              <div><div className="text-xs text-slate-500">Začátek</div><div className="flex items-center gap-1 font-bold tabular-nums text-slate-700"><Lock className="w-3.5 h-3.5" />{entry.od || "?"}</div></div>
              <div><div className="text-xs text-slate-500">Nový konec</div><div className="font-bold tabular-nums text-brand-deep">{novyKonec}</div></div>
              <div><div className="text-xs text-slate-500">Ubere se</div><div className={`font-bold tabular-nums ${stav.ubere > 0 ? "text-amber-700" : "text-slate-500"}`}>{stav.ubere > 0 ? fmtDelka(stav.ubere) : "nic"}</div></div>
            </div>
          )}

          {plna && (
            <div>
              <div className={labelCls}>Poznámka</div>
              <input value={popis} onChange={(e) => setPopis(e.target.value)} placeholder="Na čem jsi dělal(a)…" className="field" />
            </div>
          )}

          <p className="text-sm leading-snug text-slate-500">
            {ownerMode
              ? "Měření jde jen zkrátit, začátek zůstává. Dotyčnému přijde zpráva na Slack."
              : plna
                ? "Měření jde jen zkrátit, začátek zůstává. Do 2 hodin od konce jde upravit i činnost a poznámka, zkrátit do 7 dnů."
                : "Měření jde jen zkrátit, začátek zůstává. Upravit ho můžeš do 7 dnů."}
          </p>
        </div>

        {/* patička; klavesnice-ok: celý překryv okna je nad-klavesnici */}
        <div className="safe-bottom flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 border-t border-slate-100 bg-white sticky bottom-0 rounded-b-2xl">
          {plna && canDelete && onDelete ? (
            potvrdSmazat ? (
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <button onClick={onDelete} disabled={saving || busy} className="btn btn-danger flex-1 sm:flex-none">
                  <Trash2 className="w-4 h-4" /> Opravdu smazat
                </button>
                <button onClick={() => setPotvrdSmazat(false)} disabled={saving || busy}
                  className="text-sm font-semibold px-3 py-2.5 rounded-xl border border-slate-300 text-slate-600 hover:bg-slate-50 min-h-[44px]">
                  Ne
                </button>
              </div>
            ) : (
              <button onClick={() => setPotvrdSmazat(true)} disabled={saving || busy}
                className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-3 py-2.5 rounded-xl border border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-50 w-full sm:w-auto min-h-[44px]">
                <Trash2 className="w-4 h-4" /> Smazat
              </button>
            )
          ) : <span className="hidden sm:block" />}
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2">
            <button onClick={onClose} disabled={saving}
              className="text-sm font-semibold px-4 py-2.5 rounded-xl border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50 min-h-[44px]">
              Zrušit
            </button>
            <button onClick={uloz} disabled={!!stav.err || saving || busy || !zmenene} className="btn btn-primary min-h-[44px]">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Ukládám…</>
                : zkracuji ? <><Check className="w-4 h-4" /> Zkrátit na {fmtDelka(delka)}</>
                : <><Save className="w-4 h-4" /> Uložit</>}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
